import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { getAuthLoginUrl } from '@/lib/auth-callback';
import { AppLayout } from '@/components/layout/AppLayout';
import Dashboard from '@/pages/Dashboard';
import AuthCallback from '@/pages/AuthCallback';
import Contacts from '@/pages/Contacts';
import ContactDetail from '@/pages/ContactDetail';
import Organizations from '@/pages/Organizations';
import OrganizationDetail from '@/pages/OrganizationDetail';
import DealBoard from '@/pages/DealBoard';
import DealDetail from '@/pages/DealDetail';
import FormSubmissions from '@/pages/FormSubmissions';
import FormSubmissionDetail from '@/pages/FormSubmissionDetail';
import Visitors from '@/pages/Visitors';
import VisitorDetail from '@/pages/VisitorDetail';
import Settings from '@/pages/Settings';
import SequenceList from '@/pages/settings/SequenceSettings';
import SequenceDetail from '@/pages/SequenceDetail';
import SocialPosts from '@/pages/SocialPosts';
import SocialPostEditor from '@/pages/SocialPostEditor';
import SocialTemplates from '@/pages/SocialTemplates';
import SocialTemplateEditor from '@/pages/SocialTemplateEditor';
import IntakeForm from '@/pages/public/IntakeForm';
import SubmitWizard from '@/pages/public/SubmitWizard';

// Declare SolidHub global type (from app-rail)
declare global {
  interface Window {
    SolidHub?: {
      init: () => Promise<void>;
      isInitialized: () => boolean;
      getUser: () => any;
      getAccessToken: () => Promise<string | null>;
      logout: () => Promise<void>;
      getLoginUrl: (redirectUrl?: string) => string;
      extensions: {
        registerBottomNav: (item: {
          id: string;
          label: string;
          icon: string;
          tooltip?: string;
          onClick: () => void;
        }) => () => void;
        registerFlyout: (config: any) => () => void;
        registerAction: (config: any) => () => void;
      };
    };
  }
}

/**
 * Loading screen shown while waiting for authentication
 */
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-slate-400">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Hook that checks for authentication via:
 * 1. Persisted Zustand store (from OIDC callback flow)
 * 2. SolidHub app-rail (shared session from other Solid apps)
 * 3. Falls back to unauthenticated if neither is available
 */
function useAuth() {
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');
  const { accessToken, isAuthenticated, _hasHydrated, setAuth, setUser: setStoreUser } = useAuthStore();

  useEffect(() => {
    // Wait for Zustand to rehydrate from localStorage
    if (!_hasHydrated) return;

    // If we already have a valid token from OIDC callback, we're authenticated
    if (isAuthenticated && accessToken) {
      setStatus('authenticated');
      return;
    }

    // Otherwise, try app-rail
    let mounted = true;
    let checkInterval: ReturnType<typeof setInterval>;

    const checkAppRail = async () => {
      if (!window.SolidHub) return;
      if (!window.SolidHub.isInitialized()) return;

      const hubUser = window.SolidHub.getUser();

      if (hubUser && mounted) {
        const token = await window.SolidHub.getAccessToken();

        const transformedUser = {
          ...hubUser,
          organizations: hubUser.orgs?.map((o: any) => ({
            id: o.org_id,
            name: o.name,
            slug: o.slug,
            role: o.role,
            type: o.type || 'AGENCY',
          })) || [],
        };

        if (token) {
          setAuth(token, transformedUser);
          setStoreUser(transformedUser);
        }

        setStatus('authenticated');
        clearInterval(checkInterval);
      } else if (mounted) {
        setStatus('unauthenticated');
        clearInterval(checkInterval);
      }
    };

    checkAppRail();
    checkInterval = setInterval(checkAppRail, 100);

    // Shorter timeout when app-rail isn't critical (OIDC flow is primary)
    const timeout = setTimeout(() => {
      if (mounted) {
        setStatus((currentStatus) => {
          if (currentStatus === 'loading') {
            console.log('[App] App-rail not available, falling back to OIDC login');
            return 'unauthenticated';
          }
          return currentStatus;
        });
      }
    }, 3000);

    return () => {
      mounted = false;
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [_hasHydrated, isAuthenticated, accessToken, setAuth, setStoreUser]);

  return { status };
}

/**
 * Redirect legacy pre-/admin URLs (still present in old emails/Slack messages)
 * to their current /admin equivalents.
 */
function LegacyRedirect({ base }: { base: string }) {
  const { id } = useParams();
  return <Navigate to={`${base}/${id}`} replace />;
}

function AuthenticatedApp() {
  return (
    <>
      <Routes>
        <Route path="/admin" element={<AppLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="contacts/:id" element={<ContactDetail />} />
          <Route path="organizations" element={<Organizations />} />
          <Route path="organizations/:id" element={<OrganizationDetail />} />
          <Route path="deals" element={<DealBoard />} />
          <Route path="deals/:id" element={<DealDetail />} />
          <Route path="forms" element={<FormSubmissions />} />
          <Route path="forms/:id" element={<FormSubmissionDetail />} />
          <Route path="visitors" element={<Visitors />} />
          <Route path="visitors/:visitorId" element={<VisitorDetail />} />
          <Route path="sequences" element={<SequenceList />} />
          <Route path="sequences/:id" element={<SequenceDetail />} />
          <Route path="social" element={<SocialPosts />} />
          <Route path="social/new" element={<SocialPostEditor />} />
          <Route path="social/:id/edit" element={<SocialPostEditor />} />
          <Route path="social/templates" element={<SocialTemplates />} />
          <Route path="social/templates/new" element={<SocialTemplateEditor />} />
          <Route path="social/templates/:id/edit" element={<SocialTemplateEditor />} />
          <Route path="settings/*" element={<Settings />} />
        </Route>
        <Route path="/deals/:id" element={<LegacyRedirect base="/admin/deals" />} />
        <Route path="/submissions/:id" element={<LegacyRedirect base="/admin/forms" />} />
        <Route path="/forms/:id" element={<LegacyRedirect base="/admin/forms" />} />
        <Route path="/contacts/:id" element={<LegacyRedirect base="/admin/contacts" />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </>
  );
}

/**
 * Check if current path is a public route
 */
function isPublicRoute(): boolean {
  const path = window.location.pathname;
  return path === '/start' || path.startsWith('/submit/');
}

function PublicApp() {
  return (
    <>
      <Routes>
        <Route path="/start" element={<IntakeForm />} />
        <Route path="/submit/:id" element={<SubmitWizard />} />
        <Route path="*" element={<IntakeForm />} />
      </Routes>
    </>
  );
}

/**
 * Check if we're on the OAuth callback page
 */
function isCallbackPage(): boolean {
  return (
    window.location.pathname === '/auth/callback' &&
    window.location.search.includes('code=')
  );
}

export default function App() {
  const { status } = useAuth();

  // Auth callback always renders
  if (isCallbackPage()) {
    return <AuthCallback />;
  }

  // If authenticated, always show the CRM app
  if (status === 'authenticated') {
    return <AuthenticatedApp />;
  }

  // While loading auth, show loading for non-public routes
  if (status === 'loading') {
    if (isPublicRoute()) {
      return <PublicApp />;
    }
    return <LoadingScreen />;
  }

  // Unauthenticated: show public app for public routes, redirect to login for others
  if (isPublicRoute()) {
    return <PublicApp />;
  }

  // Root path shows intake form for unauthenticated users
  if (window.location.pathname === '/') {
    return <PublicApp />;
  }

  // All other unauthenticated routes → OIDC login
  getAuthLoginUrl(window.location.href).then((url) => {
    console.log('[App] Redirecting to login:', url);
    window.location.href = url;
  });
  return <LoadingScreen />;
}
